// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, StyledTypography } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getSettingsCollection } from "@werner/App/Store/Selectors/SettingsSelectors";
import Tooltip from "@werner/MobiusOverrides/Tooltip/Tooltip";
import { color, ellipsis, generateHoverStyleForLink, linkArrowStyle, mergeWithCss } from "@werner/XDL/Utilities";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImages = "showImages",
}

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailSubCategoriesStyles {
    // XNG-Changes: Update Structure
    container?: InjectableCss;
    subCategoryItem?: GridItemProps;
    innerContainer?: InjectableCss;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    imageWrapper?: InjectableCss;
}

export const categoryDetailSubCategoriesStyles: CategoryDetailSubCategoriesStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            row-gap: 64px;
            column-gap: 40px;
            margin: 64px 0;
            flex-wrap: wrap;
        `,
    },
    innerContainer: {
        css: css`
            max-width: 166px;
            max-height: 166px;
            width: 100%;
            height: 100%;
            background: ${color("common.white")};
            border: 1px solid ${color("primary", 0.05)};
            box-shadow: 0 0 15px 0 ${color("primary", 0.15)};
            &&&& .subCategoriesLink {
                display: flex;
            }
            &:hover {
                box-shadow: 0 0 15px 5px ${color("primary", 0.15)};
                transition: box-shadow 0.15s linear;
                & > .subCategoriesLink,
                ${StyledTypography}, ${IconWrapper} {
                    color: ${color("common.ui3")};
                }
                & > .subCategoriesLink,
                ${IconWrapper} {
                    @keyframes bounceright {
                        from {
                            transform: translateX(0);
                        }
                        to {
                            transform: translateX(3px);
                        }
                    }
                    animation: bounceright 0.3s alternate ease infinite;
                }
            }
        `,
    },
    subCategoryImageClickable: {
        css: css`
            max-width: 166px;
            max-height: 166px;
            height: 100%;
            width: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    subCategoryImage: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    subCategoryNameLinkItem: {
        css: css`
            margin-top: 8px;
        `,
    },
    subCategoryNameLink: {
        ...mergeWithCss(linkArrowStyle, {
            color: "primary",
            css: css`
                width: 100%;
                text-align: center;
                justify-content: center;
                & > ${TypographyStyle} {
                    line-height: 24px;
                    ${ellipsis(1)};
                }

                & > ${IconWrapper} {
                    color: ${color("primary")};
                }

                ${generateHoverStyleForLink("common.ui3")};
            `,
        }),
    },
    imageWrapper: {
        css: css`
            width: 166px;
            height: 166px;
        `,
    },
};

const styles = categoryDetailSubCategoriesStyles;

const CategoryDetailSubCategories = ({ category, fields: { showImages } }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }
    // XNG Changes: Update Structure
    return (
        <StyledWrapper {...styles.container}>
            {category.subCategoryIds.map(subCategoryId => (
                <SubCategoryLink key={subCategoryId} categoryId={subCategoryId} showImages={showImages} />
            ))}
        </StyledWrapper>
    );
};

const SubCategoryLinkView = ({
    category,
    showImages,
}: ReturnType<typeof mapStateToProps> & { showImages: boolean }) => {
    const { smallDefaultCategoryImage } = useAppSelector(state => {
        return {
            smallDefaultCategoryImage: getSettingsCollection(state).customSettings.smallDefaultCategoryImage,
        };
    });

    if (!category) {
        return null;
    }

    const smallImagePath = category.smallImagePath || smallDefaultCategoryImage;

    return (
        <StyledWrapper {...styles.innerContainer}>
            <Clickable
                href={category.path}
                {...styles.subCategoryImageClickable}
                data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                aria-label={`categoryDetailsSubCategoriesLink_${category.id}`}
            >
                {showImages && (
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage src={smallImagePath} altText={category.imageAltText} {...styles.subCategoryImage} />
                    </StyledWrapper>
                )}
            </Clickable>

            <StyledWrapper {...styles.subCategoryNameLinkItem}>
                <Tooltip
                    cssOverrides={{
                        tooltipClickable: css`
                            display: flex;
                            width: 100%;
                            justify-content: center;
                        `,
                    }}
                    placement="top"
                    triggerComponent={
                        <Link
                            href={category.path}
                            {...styles.subCategoryNameLink}
                            data-test-selector={`categoryDetailsSubCategoriesLink_${category.id}`}
                            className="subCategoriesLink"
                            aria-label={`${category.shortDescription} SubCategory Link`}
                        >
                            {category.shortDescription}
                        </Link>
                    }
                    text={category.shortDescription}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailSubCategories),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
